import { Box, Theme, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { parentManagePaymentMethodPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { useEffect } from "react";

import { usePaymentMethods } from "../../hooks/CurrentUser";

export default function BackupPaymentBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\BackupPaymentBanner",
  );
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const isMobile = useIsMobile();

  const { paymentMethods, loading: paymentMethodsLoading } =
    usePaymentMethods();

  // Track if banner view has been tracked for this mount to prevent duplicate tracking
  const hasTrackedViewRef = React.useRef<boolean>(false);

  const hasBackupPaymentMethod = React.useMemo(() => {
    return paymentMethods.some(
      paymentMethod => !!paymentMethod.isBackUpForOffSessionCharges,
    );
  }, [paymentMethods]);

  const shouldShowBanner = React.useMemo(() => {
    // Wait for payment methods to load
    if (paymentMethodsLoading) {
      return false;
    }

    // Don't show if user already has a backup payment method
    if (hasBackupPaymentMethod) {
      return false;
    }

    // Don't show if user has no payment methods at all
    if (paymentMethods.length === 0) {
      return false;
    }

    return true;
  }, [hasBackupPaymentMethod, paymentMethods.length, paymentMethodsLoading]);

  useEffect(() => {
    if (!hasTrackedViewRef.current && shouldShowBanner) {
      hasTrackedViewRef.current = true;
      trackEvent("backup_payment_banner_view", {
        is_mobile: isMobile,
      });
    }
  }, [trackEvent, isMobile, shouldShowBanner]);

  const handleBannerClick = () => {
    trackEvent("backup_payment_banner_click", {
      is_mobile: isMobile,
    });
    navigate(parentManagePaymentMethodPath());
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary[600],
        minHeight: "50px",
        cursor: "pointer",
        padding: "0 1em",
        [theme.breakpoints.down("md")]: {
          padding: "20px",
        },
      })}
      onClick={handleBannerClick}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "1440px",
          margin: "0 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              <Trans t={t}>
                <b>Never miss a class!</b> If your default card fails, we’ll
                automatically try your backup.
              </Trans>
              <Box
                component="span"
                sx={{
                  marginLeft: 8,
                  textDecoration: "underline",
                }}
              >
                {t("Add a Backup Card")}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
